import { useMemo } from 'react';
import { getImageUrl } from '@/shared/utils/image';
import type { StoreItem } from '../types/get-store-items';
import { getDisplayPrice } from '../utils/display-price';
import { getDiscountPercent, getPromotion } from '../utils/promotion';
import { PromotionBadge, PromotionPrice } from './promotion-price';

interface PromotionOfferCardProps {
  item: StoreItem;
  /** Acionado ao selecionar a oferta (ex.: abrir o produto). */
  onSelect?: (item: StoreItem) => void;
}

/**
 * Card de oferta usado na lista do modal de Promoções. Apresenta um produto em
 * promoção no layout horizontal (imagem + informações), destacando o selo de
 * desconto e a apresentação De/Por reaproveitando {@link getDisplayPrice} e os
 * componentes {@link PromotionBadge}/{@link PromotionPrice}.
 *
 * Diferente dos cards do cardápio, é renderizado como `button` para navegação
 * acessível por teclado dentro do diálogo. O preço/percentual sempre passa pela
 * validação de `utils/promotion.ts`: quando o item não tem promoção válida a
 * apresentação De/Por e o selo simplesmente não aparecem (default seguro).
 */
export function PromotionOfferCard({ item, onSelect }: PromotionOfferCardProps) {
  const { price, regularPrice, hasRequiredAddons } = useMemo(() => getDisplayPrice(item), [item]);
  const promotion = useMemo(() => getPromotion(item), [item]);

  return (
    <button
      type="button"
      onClick={() => onSelect?.(item)}
      className="flex w-full gap-3 text-left cursor-pointer p-2 rounded-lg hover:bg-gray-50 transition-colors clickable-card"
    >
      {/* Imagem do item */}
      <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-gray-100">
        <img
          loading="lazy"
          decoding="async"
          src={getImageUrl(item?.image)}
          alt={item.name}
          className="w-full h-full object-cover object-center"
          onError={e => {
            e.currentTarget.src = '/fallback.svg';
          }}
        />
      </div>

      {/* Informações do item ocupando todo o restante */}
      <div className="flex flex-1 flex-col justify-between min-w-0">
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-night truncate">{item.name}</p>
            {promotion && <PromotionBadge percent={getDiscountPercent(item)} />}
          </div>
          {item.description && (
            <p className="text-xs text-gray-500 line-clamp-2 leading-relaxed">{item.description}</p>
          )}
        </div>
        <PromotionPrice
          className="mt-1"
          price={price}
          regularPrice={regularPrice}
          prefix={hasRequiredAddons ? 'A partir de' : undefined}
        />
      </div>
    </button>
  );
}
